import * as React from 'react';
import { observer } from 'mobx-react';
import { PayerOnboardingError, PayerOnboardingErrorType, IShouldReloadPageError, IAddPaymentMethodError, IMobileNumberInUseError, IAccountAlreadyActiveError } from './payer-onboarding-error';
import { ResetPageReason } from '../../payer-onboarding-generated';
import { Alert } from '../../../WebGiving/components/alert/alert';
import { Warning } from '../warning/warning';
import { Link, LinkProps } from '../../../WebGiving/primitives/link/link';
import { scrollErrorIntoViewOnNextFrame } from '../../../WebGiving/utils/error-focuser';

import { description, nowrap } from './error-message.less';

export interface IPayerOnboardingErrorMessageProps {
	error?: PayerOnboardingError;
	onResetPage: (reason?: ResetPageReason) => void;
}

@observer
export class ErrorMessage extends React.Component<IPayerOnboardingErrorMessageProps> {

	UNSAFE_componentWillReceiveProps(nextProps: IPayerOnboardingErrorMessageProps) {
		if (nextProps.error && nextProps.error !== this.props.error) {
			scrollErrorIntoViewOnNextFrame();
		}
	}

	render() {
		const { error } = this.props;

		if (!error) {
			return null;
		}

		switch (error.errorType) {
			case PayerOnboardingErrorType.Generic:
				return <Alert content={error.message} />;
			case PayerOnboardingErrorType.ShouldReloadPage:
				return <Alert content={this.displayMarkupForReloadPageError(error)} />;
			case PayerOnboardingErrorType.AddPaymentMethodError:
				return <Alert content={this.displayMarkupForAddPaymentMethodError(error)} />;
			case PayerOnboardingErrorType.MobileNumberInUseError:
				return <Warning content={this.displayMarkupForMobileNumberInUseError(error)} />;
			case PayerOnboardingErrorType.AccountAlreadyActiveError:
				return <Warning content={this.displayMarkupForAccountAlreadyActiveError(error)} />;
			default:
				const unsupported: never = error;
				throw new Error(`Unsupported PayerOnboardingErrorType ${unsupported}`);
		}
	}

	private displayMarkupForAccountAlreadyActiveError(error: IAccountAlreadyActiveError) : React.ReactChild {
		const { message, loginUrl } = error;
		return (
			<div>
				<p className={description}>
					{message}{' '}
					<ErrorMessageLink href={loginUrl} acceptanceTestTargetId="error-login">
						Log in to update your details
					</ErrorMessageLink>
				</p>
			</div>
		);
	}

	private displayMarkupForMobileNumberInUseError(error: IMobileNumberInUseError) : React.ReactChild {
		const { message, loginUrl } = error;

		return (
			<div>
				<p className={description}>
					{message}{' '}
					<ErrorMessageLink href={loginUrl} acceptanceTestTargetId="error-login">
						Log in
					</ErrorMessageLink>
					{' '}to manage your account.
				</p>
			</div>
		);
	}

	private displayMarkupForAddPaymentMethodError(error: IAddPaymentMethodError) {
		const { errorMessageLines } = error;

		if (errorMessageLines.length === 1) {
			return errorMessageLines[0];
		}

		return (
			<div>
				{errorMessageLines.map(line => <p>{line}</p>)}
			</div>
		);
	}

	private displayMarkupForReloadPageError(error: IShouldReloadPageError) : React.ReactChild {
		const { reason } = error;

		const acceptanceTestTarget = 'error-try-again';
		switch (reason) {
			case ResetPageReason.PersonNotFound: {
				return (
					<div>
						{/* tslint:disable:max-line-length */}
						<p className={description}>Your session has expired. <ErrorMessageLink acceptanceTestTargetId={acceptanceTestTarget} onClick={this.handleTryAgain}>Please re-enter your details.</ErrorMessageLink></p>
						{/* tslint:enable:max-line-length */}
					</div>
				);
			}
			case ResetPageReason.DuplicateMobileNumber: {
				return (
					<div>
						{/* tslint:disable:max-line-length */}
						<p className={description}>This mobile number you entered has already been registered. Please <ErrorMessageLink acceptanceTestTargetId={acceptanceTestTarget} onClick={this.handleTryAgain}>try again</ErrorMessageLink> with a different mobile number.</p>
						{/* tslint:enable:max-line-length */}
					</div>
				);
			}
			case ResetPageReason.Unknown:
			default: {
				return (
					<div>
						{/* tslint:disable:max-line-length */}
						<p className={description}>Our system encountered an unexpected error. We're currently looking into it. <ErrorMessageLink acceptanceTestTargetId={acceptanceTestTarget} onClick={this.handleTryAgain}>Please re-enter your details.</ErrorMessageLink></p>
						{/* tslint:enable:max-line-length */}
					</div>
				);
			}
		}
	}

	private handleTryAgain = () => {
		const { error } = this.props;

		if (error && error.errorType === PayerOnboardingErrorType.ShouldReloadPage) {
			this.props.onResetPage(error.reason);
		} else {
			this.props.onResetPage();
		}
	}
}

const ErrorMessageLink: React.StatelessComponent<{
	acceptanceTestTargetId: LinkProps['acceptanceTestTargetId'];
} & React.AnchorHTMLAttributes<HTMLAnchorElement>> = (props) => {
	return (
		<Link {...props} branded={false} inheritFontSize={true} className={nowrap}>
			{props.children}
		</Link>
	);
};
